/** @typedef {'private' | 'tenant_public'} TenantContactDataVisibility */

export const TENANT_CONTACT_VISIBILITY = Object.freeze({
  PRIVATE: "private",
  TENANT_PUBLIC: "tenant_public",
});

/** @type {readonly TenantContactDataVisibility[]} */
export const TENANT_CONTACT_VISIBILITY_VALUES = Object.freeze([
  TENANT_CONTACT_VISIBILITY.PRIVATE,
  TENANT_CONTACT_VISIBILITY.TENANT_PUBLIC,
]);

function asStr(v) {
  return String(v ?? "").trim();
}

/**
 * @param {unknown} value
 * @returns {TenantContactDataVisibility}
 */
export function normalizeTenantContactDataVisibility(value) {
  const v = asStr(value).toLowerCase();
  if (v === TENANT_CONTACT_VISIBILITY.TENANT_PUBLIC) {
    return TENANT_CONTACT_VISIBILITY.TENANT_PUBLIC;
  }
  return TENANT_CONTACT_VISIBILITY.PRIVATE;
}

/**
 * Portal-Identifier: E-Mail (primär) oder Telefon (Fallback).
 *
 * @param {{ email?: unknown, phone?: unknown }} input
 * @returns {{ kind: 'email'|'phone'|'none', value: string }}
 */
export function resolvePortalContactIdentifier(input) {
  const email = asStr(input?.email).toLowerCase();
  if (email && email.includes("@")) {
    return { kind: "email", value: email };
  }
  const phone = normalizePortalContactPhone(input?.phone);
  if (phone) {
    return { kind: "phone", value: phone };
  }
  return { kind: "none", value: "" };
}

/**
 * @param {unknown} raw
 * @returns {string}
 */
export function normalizePortalContactPhone(raw) {
  const digits = asStr(raw).replace(/\D+/g, "");
  if (digits.length < 8) return "";
  return digits;
}

/**
 * Client-seitige Spiegelung der DB-Leselogik (Defense in Depth).
 *
 * @param {{
 *   contact: { owner_trainer_id?: string|null, tenant_data_visibility?: string|null, tenant_id?: string|null, created_by?: string|null, id?: string|null },
 *   viewerTrainerIds?: string[],
 *   isCoordinator?: boolean,
 *   isOwner?: boolean,
 *   sessionParticipantPersonIds?: string[],
 * }} input
 */
export function mayReadTenantContact({
  contact,
  viewerTrainerIds = [],
  isCoordinator = false,
  isOwner = false,
  sessionParticipantPersonIds = [],
} = {}) {
  if (!contact) return false;
  if (isOwner) return true;
  if (isCoordinator) return true;

  const ownerId = asStr(contact.owner_trainer_id);
  const visibility = normalizeTenantContactDataVisibility(
    contact.tenant_data_visibility,
  );
  const trainerIds = new Set(
    (Array.isArray(viewerTrainerIds) ? viewerTrainerIds : [])
      .map(asStr)
      .filter(Boolean),
  );

  if (ownerId && trainerIds.has(ownerId)) return true;
  if (
    visibility === TENANT_CONTACT_VISIBILITY.TENANT_PUBLIC &&
    trainerIds.size > 0
  ) {
    return true;
  }

  const personId = asStr(contact.id);
  if (
    personId &&
    trainerIds.size > 0 &&
    (Array.isArray(sessionParticipantPersonIds)
      ? sessionParticipantPersonIds
      : []
    )
      .map(asStr)
      .includes(personId)
  ) {
    return true;
  }
  return false;
}

/**
 * @param {{
 *   viewerTrainerId?: string,
 *   viewerMode?: string,
 *   fallbackTrainerId?: string,
 * }} input
 */
export function resolveContactCreateOwnershipFields({
  viewerTrainerId = "",
  viewerMode = "",
  fallbackTrainerId = "",
} = {}) {
  const ownerTrainerId =
    asStr(viewerTrainerId) ||
    (asStr(viewerMode) === "trainer" ? asStr(fallbackTrainerId) : "");
  return {
    owner_trainer_id: ownerTrainerId || null,
    tenant_data_visibility: TENANT_CONTACT_VISIBILITY.PRIVATE,
  };
}

/**
 * @param {unknown} raw
 * @returns {{ ownerTrainerId: string, tenantDataVisibility: TenantContactDataVisibility }}
 */
export function normalizeContactOwnershipFields(raw) {
  const source =
    raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  return {
    ownerTrainerId: asStr(source.owner_trainer_id ?? source.ownerTrainerId),
    tenantDataVisibility: normalizeTenantContactDataVisibility(
      source.tenant_data_visibility ?? source.tenantDataVisibility,
    ),
  };
}
